import React, { useState } from 'react';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Button, Spin, Modal, Image, Upload } from 'antd';
import useUpdateMeterDetailsBlade from './useUpdateMeterDetailsBlade';
import { getEffectiveMonthYear, isBladeMonthEditable } from '@utils/effectiveMonthYear';
import MeterDetailsBlade from '../../MeterDetailsBlade';
import BladeSection from '../../BladeSection';
import styles from './UpdateMeterDetailsBlade.module.css';
import type { MeterMonthDetails } from '@pages/meter-read-details/useMeterReadDetailsPage';

interface UpdateMeterDetailsBladeProps {
  open: boolean;
  onClose: () => void;
  id: string | number;
  meterName?: string | null;
  meterNumber?: string | null;
  monthYear?: string;
  monthData: MeterMonthDetails | null;
  isApproved?: boolean;
  onSaveMonth: (monthYear: string, input: Record<string, any>, reason?: string) => Promise<boolean>;
  onUploadComplete?: () => void;
  onMonthChange?: (monthYear: string) => void;
  saving?: boolean;
  saveFeedback?: { status: 'success' | 'error'; message: string } | null;
}

const computeScadaNet = (gen: string, used: string): string => {
  const g = Number.parseFloat(gen);
  const u = Number.parseFloat(used);
  if (Number.isNaN(g) || Number.isNaN(u)) return '';
  return String(g - u);
};

const UpdateMeterDetailsBlade = ({
  open,
  onClose,
  id,
  meterName,
  meterNumber,
  monthYear,
  monthData,
  isApproved: isApprovedProp,
  onSaveMonth,
  onUploadComplete,
  onMonthChange,
  saving = false,
  saveFeedback,
}: UpdateMeterDetailsBladeProps) => {
  const monthLabel = monthYear ? dayjs(monthYear, 'YYYY-MM').format('MMMM YYYY') : '—';
  const effectiveMonthYear = getEffectiveMonthYear();
  const bladeEditable = isBladeMonthEditable();
  const isEffectiveMonth = bladeEditable || monthYear === effectiveMonthYear;
  const isApproved = isApprovedProp ?? false;
  const isProdStatApproved = monthData?.approvalStatus === 'Approved';

  const propKwh = monthData?.manualKwhAdjustment != null ? String(monthData.manualKwhAdjustment) : '';
  const propComments = monthData?.manualKwhAdjustmentComments ?? '';
  const propScadaGenerated = monthData?.scadaGenerated != null ? String(monthData.scadaGenerated) : '';
  const propScadaUsed = monthData?.scadaUsed != null ? String(monthData.scadaUsed) : '';
  const hasFile = !!monthData?.scadaSupportFile;

  const [kwhValue, setKwhValue] = useState(propKwh);
  const [commentsValue, setCommentsValue] = useState(propComments);
  const [scadaGeneratedValue, setScadaGeneratedValue] = useState(propScadaGenerated);
  const [scadaUsedValue, setScadaUsedValue] = useState(propScadaUsed);
  const [commentsError, setCommentsError] = useState(false);
  const [scadaGeneratedError, setScadaGeneratedError] = useState(false);
  const [scadaUsedError, setScadaUsedError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [scadaCleared, setScadaCleared] = useState(false);

  const isDirty =
    kwhValue !== propKwh ||
    commentsValue !== propComments ||
    scadaGeneratedValue !== propScadaGenerated ||
    scadaUsedValue !== propScadaUsed;

  const scadaNetDisplay = computeScadaNet(scadaGeneratedValue, scadaUsedValue);

  const fileData = monthYear
    ? { value: monthData?.scadaSupportFile ?? null, isApproved, month_year: monthYear, id: monthData?.scadaUploadId ?? null }
    : null;

  const { triggerUpload, uploading, handleDownload, uploadSuccess, scanning, scadaScanError } = useUpdateMeterDetailsBlade({
    data: fileData,
    id,
    isEffectiveMonth,
    isApproved,
    onUploadComplete,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [scadaConfirmPending, setScadaConfirmPending] = useState(false);

  const onViewClick = async () => {
    setViewLoading(true);
    const url = await handleDownload();
    setViewLoading(false);
    if (url) { setImageUrl(url); setIsModalOpen(true); }
  };

  React.useEffect(() => {
    if (open) {
      setKwhValue(propKwh);
      setCommentsValue(propComments);
      setScadaGeneratedValue(propScadaGenerated);
      setScadaUsedValue(propScadaUsed);
      setCommentsError(false);
      setScadaGeneratedError(false);
      setScadaUsedError(false);
      setFileError(false);
    }
  }, [open, monthYear]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (hasFile) setFileError(false);
  }, [hasFile]);

  React.useEffect(() => {
    if (saveFeedback?.status === 'success') {
      const t = setTimeout(() => onClose(), 1500);
      return () => clearTimeout(t);
    }
  }, [saveFeedback, onClose]);

  const validateScadaFields = (skipFileValidation: boolean, hasEitherScada: boolean): boolean => {
    let hasError = false;
    if (!skipFileValidation && hasEitherScada && scadaGeneratedValue === '') {
      setScadaGeneratedError(true);
      hasError = true;
    }
    if (!skipFileValidation && hasEitherScada && scadaUsedValue === '') {
      setScadaUsedError(true);
      hasError = true;
    }
    if (!skipFileValidation && !hasFile && !bladeEditable) {
      setFileError(true);
      hasError = true;
    }
    return hasError;
  };

  const doSave = async () => {
    if (!monthYear) return;

    const input: Record<string, any> = {};
    if (kwhValue !== propKwh) input.manualKwhAdjustment = kwhValue === '' ? null : Number(kwhValue);
    if (commentsValue !== propComments) input.manualKwhAdjustmentComments = commentsValue.trim() || null;
    const hasActiveScadaRow = !!(monthData?.scadaUploadId ?? hasFile);
    if (scadaCleared && hasActiveScadaRow) {
      // Force-send nulls so backend knows to archive the SCADA row even if values were already null
      input.scadaGenerated = null;
      input.scadaUsed = null;
      if (hasFile) input.scadaSupportFile = null;
    } else {
      if (scadaGeneratedValue !== propScadaGenerated) input.scadaGenerated = scadaGeneratedValue === '' ? null : Number(scadaGeneratedValue);
      if (scadaUsedValue !== propScadaUsed) input.scadaUsed = scadaUsedValue === '' ? null : Number(scadaUsedValue);

      // When both SCADA fields end up null (cleared), also clear the support file
      const finalScadaGen = 'scadaGenerated' in input ? input.scadaGenerated : (propScadaGenerated === '' ? null : Number(propScadaGenerated));
      const finalScadaUsed = 'scadaUsed' in input ? input.scadaUsed : (propScadaUsed === '' ? null : Number(propScadaUsed));
      const clearingScada = finalScadaGen == null && finalScadaUsed == null && (propScadaGenerated !== '' || propScadaUsed !== '');
      if (clearingScada && hasFile) input.scadaSupportFile = null;
    }

    if (Object.keys(input).length === 0) { onClose(); return; }

    await onSaveMonth(monthYear, input);
  };

  const handleApply = async () => {
    if (!monthYear) return;
    let hasError = false;

    const skipFileValidation = isApproved || isProdStatApproved;
    if (kwhValue !== '' && !commentsValue.trim()) { setCommentsError(true); hasError = true; }
    const hasEitherScada = scadaGeneratedValue !== '' || scadaUsedValue !== '';
    if (validateScadaFields(skipFileValidation, hasEitherScada)) hasError = true;
    if (hasError) return;

    // Warn on first-time SCADA entry (prop values were empty, now setting values)
    const settingScadaForFirstTime =
      hasEitherScada &&
      propScadaGenerated === '' && propScadaUsed === '';

    if (settingScadaForFirstTime) {
      setScadaConfirmPending(true);
      return;
    }

    await doSave();
  };

  const uploadButtonLabel = uploading ? 'Uploading...' : scanning ? 'Scanning...' : hasFile ? 'Replace File' : 'Upload File';

  const modal = (
    <Modal
      open={isModalOpen}
      onCancel={() => { setIsModalOpen(false); setImageUrl(null); }}
      footer={[<Button key="close" onClick={() => { setIsModalOpen(false); setImageUrl(null); }} data-testid="modal-close-button">Close</Button>]}
      width={800}
      centered
      wrapProps={{ 'data-testid': 'screenshot-modal' }}
    >
      {imageUrl && <Image src={imageUrl} alt="Screenshot" style={{ width: '100%' }} preview={false} data-testid="screenshot-image" />}
    </Modal>
  );

  const scadaConfirmModal = (
    <Modal
      open={scadaConfirmPending}
      title="Set Meter Read Type to SCADA"
      zIndex={10002}
      onCancel={() => setScadaConfirmPending(false)}
      footer={[
        <Button key="cancel" onClick={() => setScadaConfirmPending(false)} data-testid="modal-cancel-button">Cancel</Button>,
        <Button key="confirm" type="primary" onClick={() => { setScadaConfirmPending(false); doSave(); }} data-testid="modal-ok-button">Confirm</Button>,
      ]}
      centered
      wrapProps={{ 'data-testid': 'scada-confirm-modal' }}
    >
      Setting SCADA values will change this meter's read type to SCADA. This cannot be undone — the original meter read values will be overwritten and cannot be recovered.
    </Modal>
  );

  return (
    <MeterDetailsBlade
      title="Updated Meter Details"
      open={open}
      onClose={onClose}
      meterName={meterName}
      meterNumber={meterNumber}
      monthYear={monthYear}
      monthLabel={monthLabel}
      onMonthChange={onMonthChange}
      handleApply={handleApply}
      saving={saving}
      isDirty={isDirty}
      readOnly={isApproved}
      saveFeedback={saveFeedback}
      accessoryView={<>{modal}{scadaConfirmModal}</>}
    >
      <BladeSection
        title="Manual kWh Adjustment"
        onClear={() => {
          setKwhValue('');
          setCommentsValue('');
          setCommentsError(false);
        }}
        showClear={(kwhValue !== '' || commentsValue !== '') && !isApproved}
      >
        <div className={styles.fieldGroup}>
          <div className={styles.fieldRow}>
            <label htmlFor="kwh-adjustment" className={styles.fieldLabel}>kWh Adjustment</label>
            <input
              id="kwh-adjustment"
              data-testid="kwh-adjustment-input"
              type="number"
              className={styles.fieldInput}
              placeholder="Enter value"
              value={kwhValue}
              readOnly={isApproved}
              style={isApproved ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
              onChange={(e) => { if (!isApproved) setKwhValue(e.target.value); }}
            />
          </div>
          <div className={styles.fieldRow}>
            <label htmlFor="kwh-comments" className={styles.fieldLabel}>
              Comments
              {kwhValue !== '' && !isApproved && <span className={styles.required}> *</span>}
            </label>
            <input
              id="kwh-comments"
              data-testid="kwh-comments-input"
              data-invalid={String(commentsError)}
              type="text"
              className={[styles.fieldInput, commentsError ? styles.error : ''].filter(Boolean).join(' ')}
              placeholder="Enter comments (mandatory)"
              maxLength={200}
              value={commentsValue}
              readOnly={isApproved}
              style={isApproved ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
              onChange={(e) => {
                if (isApproved) return;
                const val = e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, '');
                setCommentsValue(val);
                if (val.trim()) setCommentsError(false);
              }}
            />
          </div>
        </div>
      </BladeSection>

      <BladeSection
        title="Meter Values"
        onClear={() => {
          setScadaGeneratedValue('');
          setScadaUsedValue('');
          setScadaGeneratedError(false);
          setScadaUsedError(false);
          setScadaCleared(true);
        }}
        showClear={(scadaGeneratedValue !== '' || scadaUsedValue !== '') && !isApproved && !isProdStatApproved}
      >
        <div className={styles.fieldGroup}>
          <div className={styles.fieldRow}>
            <label htmlFor="scada-generated" className={styles.fieldLabel}>SCADA Generated</label>
            <input
              id="scada-generated"
              data-testid="scada-generated-input"
              data-invalid={String(scadaGeneratedError)}
              type="number"
              className={[styles.fieldInput, scadaGeneratedError ? styles.error : ''].filter(Boolean).join(' ')}
              placeholder="Enter value"
              value={scadaGeneratedValue}
              readOnly={isApproved}
              style={isApproved ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
              onChange={(e) => {
                if (isApproved) return;
                setScadaGeneratedValue(e.target.value);
                if (e.target.value) { setScadaGeneratedError(false); setScadaCleared(false); }
              }}
            />
          </div>
          <div className={styles.fieldRow}>
            <label htmlFor="scada-used" className={styles.fieldLabel}>SCADA Used</label>
            <input
              id="scada-used"
              data-testid="scada-used-input"
              data-invalid={String(scadaUsedError)}
              type="number"
              className={[styles.fieldInput, scadaUsedError ? styles.error : ''].filter(Boolean).join(' ')}
              placeholder="Enter value"
              value={scadaUsedValue}
              readOnly={isApproved}
              style={isApproved ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
              onChange={(e) => {
                if (isApproved) return;
                setScadaUsedValue(e.target.value);
                if (e.target.value) { setScadaUsedError(false); setScadaCleared(false); }
              }}
            />
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.fieldLabel}>SCADA Net</label>
            <div className={styles.readonlyValue} data-testid="scada-net-value">{scadaNetDisplay !== '' ? scadaNetDisplay : '—'}</div>
          </div>
        </div>
      </BladeSection>

      <BladeSection title={<>SCADA Support File<span className={styles.required}> *</span></>} sectionId="scada-support-file">
        <div className={styles.fileUploadWrapper}>
          {fileError && !bladeEditable && <div className={styles.errorText} data-testid="scada-file-error">A SCADA support file is required.</div>}
          {scanning && <div className={styles.infoText} data-testid="scada-scanning">Scanning file for viruses before saving…</div>}
          {scadaScanError && <div className={styles.errorText} data-testid="scada-scan-error">{scadaScanError}</div>}
          {!scadaCleared && (hasFile || uploadSuccess || isApproved) && (
            <Button
              type="link"
              icon={viewLoading ? <Spin size="small" /> : <EyeOutlined />}
              onClick={onViewClick}
              disabled={viewLoading}
              style={{ paddingLeft: 0 }}
              data-testid="scada-file-view-button"
            >
              {viewLoading ? 'Loading...' : 'View file'}
            </Button>
          )}
          {isEffectiveMonth && !isApproved && (
            <Upload showUploadList={false} beforeUpload={triggerUpload} disabled={uploading || scanning} data-testid="scada-file-input">
              <Button
                icon={(uploading || scanning) ? <Spin size="small" /> : <UploadOutlined />}
                disabled={uploading || scanning}
                data-testid="scada-upload-button"
                data-action={hasFile ? 'replace' : 'upload'}
              >
                {uploadButtonLabel}
              </Button>
            </Upload>
          )}
        </div>
      </BladeSection>
    </MeterDetailsBlade>
  );
};

export default UpdateMeterDetailsBlade;
